import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ReactComponent as GhostLogo } from '../../assets/logo-4.svg';
import './animated-ataric.css';

type IdleBehavior = 'idle' | 'blink' | 'bounce' | 'wave' | 'look' | 'excited' | 'celebrate' | 'dance' | 'spookyFloat' | 'ghostPoof' | 'welcomeEntrance';
type PageContext = 'home' | 'conversation' | 'chat' | 'skills' | 'plans' | 'celebration';

interface AnimatedAtaricProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
  showMessages?: boolean;
  behavior?: IdleBehavior;
  autoAnimate?: boolean;
  context?: PageContext;
}

// Time of day helper
function getTimeOfDay(): 'morning' | 'afternoon' | 'night' {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  return 'night';
}

// Get greeting based on time
function getTimeGreeting(): string {
  const time = getTimeOfDay();
  if (time === 'morning') return 'Bom dia! ☀️';
  if (time === 'afternoon') return 'Boa tarde! 🌤️';
  return 'Boa noite! 🌙';
}

// Context-aware messages
function getMessagesForContext(context: PageContext): { greeting: string; messages: string[] } {
  const time = getTimeOfDay();
  const timeGreeting = getTimeGreeting();
  
  switch (context) {
    case 'home':
      return {
        greeting: timeGreeting,
        messages: [
          timeGreeting,
          'Bem-vindo! 👋',
          'Bora praticar? 🚀',
          'Escolha uma opção! 🎯',
          'Que bom te ver! 😊',
          'Ready to learn? 📚',
          ...(time === 'night' ? ['Ainda acordado? 🌙'] : []),
          ...(time === 'morning' ? ['Novo dia, novo aprendizado! 🌅'] : []),
        ]
      };
    
    case 'conversation':
      return {
        greeting: 'Escolha um modo! 🎮',
        messages: [
          'Qual modo hoje? 🤔',
          'Beginner ou Advanced? 🎯',
          'Escolha seu desafio! 💪',
          'Pronto pra conversar? 💬',
          'Let\'s practice! 🗣️',
          'Eu acredito em você! ⭐',
          'Vamos lá! 🚀',
        ]
      };
    
    case 'chat':
      return {
        greeting: 'Vamos conversar! 🎤',
        messages: [
          'Clica em iniciar! 🎯',
          'I\'m ready! 🎤',
          'Let\'s talk! 🗣️',
          'Bora praticar! 💪',
          'Estou te esperando! 😊',
          'Press start! ▶️',
          'Sem medo! 💪',
          'You can do it! ⭐',
          'Fale comigo! 🎙️',
        ]
      };
    
    case 'skills':
      return {
        greeting: 'Veja seu progresso! 📊',
        messages: [
          'Olha só! 📈',
          'Você está evoluindo! 🌟',
          'Muito bem! 👏',
          'Keep improving! 💪',
          'Orgulho de você! 🏆',
          'Great progress! ⭐',
        ]
      };
    
    case 'plans':
      return {
        greeting: 'Investindo em você! 💎',
        messages: [
          'Bom investimento! 💎',
          'Você merece! ⭐',
          'Level up! 🚀',
          'Unlock features! 🔓',
        ]
      };
    
    case 'celebration':
      return {
        greeting: 'Parabéns! 🎉',
        messages: [
          'Incrível! 🎉',
          'Mandou bem! 🏆',
          'Amazing! ⭐',
          'You did it! 💪',
        ]
      };
    
    default:
      return {
        greeting: timeGreeting,
        messages: [
          timeGreeting,
          'Hey! 👋',
          '😊',
          'Continue assim! 💪',
          'Você é incrível! 🌟',
          'You got this! 💪',
        ]
      };
  }
}

function getRandomIdleMessage(context: PageContext = 'home'): string {
  const { messages } = getMessagesForContext(context);
  return messages[Math.floor(Math.random() * messages.length)];
}

function getGreeting(context: PageContext = 'home'): string {
  const { greeting } = getMessagesForContext(context);
  return greeting;
}

function AnimatedAtaric({ 
  size = 'medium', 
  className = '', 
  showMessages = true,
  behavior: forcedBehavior,
  autoAnimate = true,
  context = 'home'
}: AnimatedAtaricProps) {
  const [behavior, setBehavior] = useState<IdleBehavior>(forcedBehavior || 'idle');
  const [message, setMessage] = useState<string>('');
  const [showBubble, setShowBubble] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isWelcoming, setIsWelcoming] = useState(false);
  const [showMatrixRain, setShowMatrixRain] = useState(false);

  // Show initial greeting when component mounts
  useEffect(() => {
    if (!showMessages || forcedBehavior) return;
    
    if (context === 'home') {
      // Matrix rain forever on home page!
      setShowMatrixRain(true);
      setIsWelcoming(true);
      setBehavior('welcomeEntrance');
      
      // Show welcome message sequence
      setTimeout(() => {
        setMessage('Olá! 👋');
        setShowBubble(true);
      }, 800);
      
      setTimeout(() => {
        setMessage('Eu sou o ATARIC! 👻');
      }, 2500);
      
      setTimeout(() => {
        setMessage('Vou te ajudar a praticar inglês! 🇺🇸');
      }, 4500);
      
      setTimeout(() => {
        setMessage('Bora começar? 🚀');
      }, 6500);
      
      setTimeout(() => {
        setBehavior('idle');
        setShowBubble(false);
        setIsWelcoming(false);
        // Matrix rain continues! Don't set showMatrixRain to false
      }, 8500);
    } else {
      // Regular greeting for other pages
      const greetingTimeout = setTimeout(() => {
        setMessage(getGreeting(context));
        setShowBubble(true);
        setBehavior('wave');
        
        setTimeout(() => {
          setBehavior('idle');
          setShowBubble(false);
        }, 3000);
      }, 1000);
      
      return () => clearTimeout(greetingTimeout);
    }
  }, [context]);

  // Random idle behaviors and messages
  useEffect(() => {
    if (!autoAnimate || forcedBehavior) return;
    
    const idleInterval = setInterval(() => {
      if (behavior === 'idle' && !isHovered) {
        const behaviors: IdleBehavior[] = ['blink', 'bounce', 'wave', 'look', 'idle', 'idle'];
        const randomBehavior = behaviors[Math.floor(Math.random() * behaviors.length)];
        
        if (randomBehavior !== 'idle') {
          setBehavior(randomBehavior);
          
          // Show a message most of the time when doing an action
          if (showMessages && Math.random() > 0.3) {
            setMessage(getRandomIdleMessage(context));
            setShowBubble(true);
            setTimeout(() => {
              if (!isHovered) setShowBubble(false);
            }, 3500);
          }
          
          setTimeout(() => setBehavior('idle'), 1500);
        }
      }
    }, 3500);

    return () => clearInterval(idleInterval);
  }, [behavior, isHovered, autoAnimate, forcedBehavior, showMessages, context]);

  // Periodic messages even without behavior change
  useEffect(() => {
    if (!showMessages || forcedBehavior || !autoAnimate) return;
    
    const messageInterval = setInterval(() => {
      if (!isHovered && !showBubble) {
        // 50% chance to show a random message
        if (Math.random() > 0.5) {
          setMessage(getRandomIdleMessage(context));
          setShowBubble(true);
          
          setTimeout(() => {
            if (!isHovered) setShowBubble(false);
          }, 3000);
        }
      }
    }, 8000);

    return () => clearInterval(messageInterval);
  }, [isHovered, showBubble, showMessages, forcedBehavior, autoAnimate, context]);

  // Use forced behavior if provided
  useEffect(() => {
    if (forcedBehavior) {
      setBehavior(forcedBehavior);
    }
  }, [forcedBehavior]);

  function handleClick() {
    if (forcedBehavior || isWelcoming) return; // Don't change if forced or during welcome
    
    // Ghost poof animation on click!
    setBehavior('ghostPoof');
    if (showMessages) {
      const ghostMessages = ['Boo! 👻', 'Poof! ✨', 'Hehe! 👻', '👻💨'];
      setMessage(ghostMessages[Math.floor(Math.random() * ghostMessages.length)]);
      setShowBubble(true);
    }
    
    setTimeout(() => {
      setBehavior(isHovered ? 'spookyFloat' : 'idle');
      if (!isHovered) setShowBubble(false);
    }, 1000);
  }

  function handleMouseEnter() {
    if (forcedBehavior || isWelcoming) return; // Don't change if forced or during welcome
    
    setIsHovered(true);
    if (showMessages) {
      setMessage(getRandomIdleMessage(context));
      setShowBubble(true);
    }
    // Spooky ghost float on hover
    setBehavior('spookyFloat');
  }

  function handleMouseLeave() {
    if (forcedBehavior) return;
    
    setIsHovered(false);
    setShowBubble(false);
    setBehavior('idle');
  }

  // Animation variants for different behaviors
  const getAnimationVariant = () => {
    switch (behavior) {
      case 'blink':
        return { scaleY: [1, 0.1, 1], transition: { duration: 0.2 } };
      case 'bounce':
        return { y: [0, -10, 0], transition: { duration: 0.4 } };
      case 'wave':
        return { rotate: [0, -15, 15, -15, 0], transition: { duration: 0.6 } };
      case 'look':
        return { x: [0, 5, -5, 0], transition: { duration: 0.8 } };
      case 'excited':
        return { 
          y: [0, -5, 0], 
          scale: [1, 1.1, 1],
          transition: { duration: 0.3, repeat: Infinity } 
        };
      case 'celebrate':
        return { 
          y: [0, -15, 0],
          rotate: [0, -10, 10, -10, 0],
          scale: [1, 1.2, 1],
          transition: { duration: 0.5, repeat: Infinity }
        };
      case 'dance':
        return {
          y: [0, -10, 0, -10, 0],
          rotate: [0, -20, 20, -20, 0],
          scale: [1, 1.1, 1, 1.1, 1],
          transition: { duration: 0.8 }
        };
      case 'spookyFloat':
        // Gentle float with slight pulse - friendly ghost hover
        return {
          y: [0, -5, 0],
          scale: [1, 1.08, 1],
          transition: { 
            duration: 1.2,
            repeat: Infinity,
            ease: "easeInOut"
          }
        };
      case 'ghostPoof':
        // Ghost vanish and reappear!
        return {
          scale: [1, 1.3, 0.2, 0, 0.5, 1.1, 1],
          opacity: [1, 0.8, 0.3, 0, 0.5, 1, 1],
          rotate: [0, 10, -180, -360, -360, -360],
          y: [0, -20, -30, -20, 0, -5, 0],
          transition: { 
            duration: 1,
            times: [0, 0.15, 0.3, 0.4, 0.6, 0.8, 1],
            ease: "easeInOut"
          }
        };
      case 'welcomeEntrance':
        // Matrix-style entrance - pet just floats gently while rain falls
        return {
          opacity: [0, 1],
          y: [10, 0],
          transition: { 
            duration: 1,
            ease: "easeOut",
          }
        };
      default:
        return { 
          y: [0, -3, 0],
          transition: { duration: 2, repeat: Infinity, ease: "easeInOut" }
        };
    }
  };

  // Get sparkles based on behavior
  const getSparkles = () => {
    if (behavior === 'welcomeEntrance') {
      return [];
    }
    if (behavior === 'celebrate' || behavior === 'dance') {
      return ['✨', '🎉', '⭐', '✨'];
    }
    if (behavior === 'ghostPoof') {
      return ['👻', '✨', '💨', '👻'];
    }
    if (behavior === 'spookyFloat') {
      return ['✨'];
    }
    if (behavior === 'excited') {
      return ['✨', '⭐'];
    }
    return [];
  };

  const sparkles = getSparkles();

  const sizeClass = `animated-ataric-${size}`;

  // User names for the rain effect
  const userNames = [
    'Áxel', 'Pedro', 'Ataric', 'Thayza', 'Cauan',
    'Victor', 'Isabella', 'Tiago', 'Rúbia', 'Ester', 'Eloísa', 'Anderson',
    'Andrea', 'Marco', 'Joao', 'Pedro',
    'Vitor', 'Vanessa', 'Iasmin', 'Roberta', 'Matheus',
    'Aniky', 'Andreia', 'Débora', 'Douglas', 'Leonardo', 'Carol', 'Egabrieli'
  ];
  
  // Generate matrix rain columns - pick a random name and split into characters (uppercase)
  const matrixColumns = showMatrixRain ? Array.from({ length: 8 }, (_, colIndex) => {
    const randomName = userNames[Math.floor(Math.random() * userNames.length)].toUpperCase();
    return {
      id: colIndex,
      chars: randomName.split(''),
      left: `${5 + (colIndex / 7) * 90}%`,
      delay: Math.random() * 2,
      duration: 4 + Math.random() * 2
    };
  }) : [];

  return (
    <div 
      className={`animated-ataric-container ${sizeClass} ${className} ${behavior !== 'idle' ? 'animated-ataric-active' : ''} ${showMatrixRain ? 'animated-ataric-matrix' : ''}`}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Matrix Rain Effect */}
      {showMatrixRain && (
        <div className="matrix-rain-container">
          {matrixColumns.map((col) => (
            <motion.div
              key={col.id}
              className="matrix-rain-column"
              style={{ left: col.left }}
              initial={{ y: -100, opacity: 0 }}
              animate={{ y: 200, opacity: [0, 1, 1, 0] }}
              transition={{
                duration: col.duration,
                delay: col.delay,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              {col.chars.map((char, i) => (
                <span 
                  key={i} 
                  className="matrix-char"
                  style={{ 
                    opacity: 1 - (i * 0.06),
                    textShadow: i === 0 ? '0 0 10px #7932b6, 0 0 20px #7932b6' : 'none'
                  }}
                >
                  {char}
                </span>
              ))}
            </motion.div>
          ))}
        </div>
      )}

      {/* Speech bubble */}
      {showMessages && (
        <AnimatePresence>
          {showBubble && message && (
            <motion.div 
              className="animated-ataric-bubble"
              initial={{ opacity: 0, scale: 0.8, y: 5 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 5 }}
              key={message}
            >
              {message}
            </motion.div>
          )}
        </AnimatePresence>
      )}
      
      {/* The pet - SVG Ghost */}
      <motion.div 
        className={`animated-ataric-pet animated-ataric-pet-${behavior}`}
        animate={getAnimationVariant()}
      >
        <GhostLogo className="animated-ataric-svg" />
        
        {/* Sparkles */}
        {sparkles.map((sparkle, i) => (
          <motion.span 
            key={i}
            className={`animated-ataric-sparkle sparkle-${i + 1}`}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: [0, 1, 0],
              scale: [0.5, 1.2, 0.5],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              delay: i * 0.2
            }}
          >
            {sparkle}
          </motion.span>
        ))}
      </motion.div>
    </div>
  );
}

export default AnimatedAtaric;
